import { Dialog } from "@omnia/shared/shell/dialog"
import { getRequirementsActivity } from "../../activity"
import { calculateNBreakevenAdjustment, getDefaultNBreakeven } from "../../nbreakeven"
import { defineDirective, getDistinctValues, handleApiError, IOmniaShell, IParam, isArray, ITab, labels, param } from "../../omnia"
import { CroppingGroupService } from "../../services/grouping"
import { IFieldsService, INutritionService } from "../../types"
import { LimeEnabledFromYear } from "../../utils"
import { ConfigVm } from "./config-vm"
import { ConfigGroup } from "./config-vm-group"
import { applyOptions, CalculationOptions, cutOffOptions, NutrientGroupOptions, setupOptions, updateOptions } from "./options"
import { isCropIgnoredForNutrition, isCropSupportedForNutrition } from "@omnia/shared/nutrition"


type Tab = Readonly<ITab & { step: number }>

export class ConfigDlg extends Dialog {

    static create() {
        return defineDirective(ConfigDlg, "app/nutrition/requirements/config/config.html")
    }

    public readonly tabs: Tab[] = [
        { step: 1, text: "Crops", key: "crops" },
        { step: 2, text: "Options", key: "options" }
    ]

    public currentTab: Tab

    private config: ConfigVm[]
    public groups: ConfigGroup[]

    configModes: IParam[]

    public enableLime: boolean
    private _showLimeOptions: boolean

    public options: CalculationOptions
    public optionsArray: NutrientGroupOptions[]

    public nbreakeven = getDefaultNBreakeven()

    public cutOffOptions = [...cutOffOptions]

    static $inject = [
        "$scope",
        "OmniaNutritionService",
        "OmniaFieldsService",
        "OmniaNutritionCroppingGroup",
        "OmniaShell"
    ]

    constructor(
        $scope: ng.IScope,
        private svc: INutritionService,
        private fieldSvc: IFieldsService,
        private groupingSvc: CroppingGroupService,
        shell: IOmniaShell) {

        super($scope, shell)

    }

    $onInit() {

        this.enableLime = (this.svc.yearId >= LimeEnabledFromYear)

        this.currentTab = this.tabs[0]

        this.setupConfigModes()
        this.setupConfig()

        this.setActivity(getRequirementsActivity())

    }

    private setupConfig() {

        const ids: number[] = isArray(this.arguments) ? this.arguments : null

        const crops = this.fieldSvc.cropping
            .filter(c => c.currentYear)
            .filter(c => c.authorized)
            .filter(c => isCropSupportedForNutrition(c))

        return this.svc.getNutritionLayers()
            .then(layers => {

                const requirementsProvider = this.svc.getRequirementsProvider()

                const lime = this.showLimeOptions = this.enableLime && this.svc.settings.get("config.lime")

                this.config = ConfigVm.create(this.svc, crops, requirementsProvider, layers, lime)

                if (ids) {
                    this.config.forEach(c => c.selected = ids.indexOf(c.croppingId) >= 0)
                }
                else {
                    this.config.forEach(c => c.selected = (!c.hasRequirements))
                }

                this.config.forEach(c => c.selected = c.selected && (!isCropIgnoredForNutrition(c.cropId)))

                this.setupOptions()

                this.updateGroups()

                this.setupNBreakeven()

            })

    }

    private setupOptions() {
        const config = this.config.filter(c => c.selected && (!!c.lastUpdate))
        const { options, cutOff } = setupOptions(config)
        this.options = options
        this.cutOffOptions = cutOff
        this.updateOptionsArray()
    }

    private updateOptionsArray() {
        if (this.options) {
            const { ns, pk, lime } = this.options
            this.optionsArray = [ns, pk, this._showLimeOptions ? lime : null].filter(i => !!i)
        }
    }

    private setupConfigModes() {
        this.configModes = param.NtrReqConfigMode()
    }

    private setupNBreakeven() {

        const config = this.config
            .filter(c => c.selected && (!!c.lastUpdate))
            .map(c => c.nBreakEvenConfig)

        const distinct = getDistinctValues(config, c => c ? JSON.stringify(c) : "")

        this.nbreakeven = (distinct.length === 1) && Boolean(distinct[0])
            ? config[0]
            : this.nbreakeven

    }

    get showLimeOptions(): boolean {
        return this._showLimeOptions
    }

    set showLimeOptions(value: boolean) {

        this._showLimeOptions = value
        this.svc.settings.set("config.lime", value)

        if (this.groups) {
            this.groups.forEach(g => g.showLimeOptions = value)
        }

        this.updateOptionsArray()

    }


    get groupings() {
        return this.groupingSvc.groupings
    }

    get currentGrouping() {
        return this.groupingSvc.currentGrouping
    }

    set currentGrouping(value) {
        this.groupingSvc.currentGrouping = value
        this.updateGroups()
    }

    get showTerraMapOption() {
        return this.config && this.config.some(c => c.showTerraMapOption)
    }

    private updateGroups() {

        let grouping = this.currentGrouping || this.groupingSvc.groupings[0]
        let groups = grouping.grouping(this.config)

        this.groups = groups.map(g => {

            let groupVm = new ConfigGroup(g.key, g.cropping, this._showLimeOptions)

            g.copyTo(groupVm)

            return groupVm

        })

    }

    getConfigModeText(mode: number): string {
        return labels.NtrReqConfigMode(mode)
    }

    getGroupConfigModes(group: NutrientGroupOptions) {

        const modes = getDistinctValues(
            this.config
                .filter(c => c.selected)
                .map(g => g.getNutrientGroupMode(group)),
            m => m
        )

        const result = [group.wholeField, group.precision, group.soilAnalysis].filter(m => m && (modes.indexOf(m.mode) >= 0))

        return result

    }

    canSelectAll() {
        return this.config && this.config.some(c => !c.selected)
    }

    selectAll(select: boolean) {
        this.config.forEach(c => c.selected = select)
        this.groups.forEach(g => g.synchronize())
    }

    updateNBreakevenAdj() {
        const { nbreakeven: nbe } = this
        nbe.NAdjustment = Math.min(0, calculateNBreakevenAdjustment(nbe.GrainPrice, nbe.NPrice))
    }

    private commitToConfigTab() {

        const cfg = this.config || []
        const step = this.currentTab ? this.currentTab.step : 1

        if ((step == 1) && cfg.some(c => requiresConfigStep(c, this._showLimeOptions))) {
            return true
        }

    }


    getCommitButtonText() {

        return this.commitToConfigTab()
            ? "Next"
            : "Calculate"

    }

    canCommit(): boolean {

        const { config, nbreakeven: nbe } = this

        if (!config) {
            return false
        }

        const selected = config.filter(c => c.selected)
        if (!selected.length) {
            return false
        }

        if (!selected.every(c => (!!c.nConfigMode) && (!!c.pkConfigMode) && ((!this._showLimeOptions) || (!!c.limeConfigMode)))) {
            return false
        }

        if (nbe?.Enabled) {

            const grain = nbe.GrainPrice || 0
            const n = nbe.NPrice || 0

            if ((grain <= 0) || (n <= 0)) {
                return false
            }

        }

        return true

    }

    commit() {

        if (!this.canCommit()) {
            this.showValidationErrors = true
            return
        }

        this.showValidationErrors = false

        if (this.commitToConfigTab()) {
            updateOptions(this.options, this.config)
            this.currentTab = this.tabs.find(t => t.step == 2)
            return
        }

        let config = this.config.filter(c => c.selected).map(c => c.getApiModel())

        applyOptions(config, this.options, this.nbreakeven, this.cutOffOptions)

        this.svc.calculateRequirements(config)
            .then(() => this.close(1))
            .catch(r => handleApiError(this.shell, r, { defaultMessage: "An error has occurred while calculating the requirements" }))

    }
}

function requiresConfigStep(cfg: ConfigVm, lime: boolean) {
    if (!cfg.selected) return false
    if (cfg.usesPrecision(lime)) return true
    if (cfg.terraMap) return true
    return false
}
